/**
 * Tiny markdown renderer — no external deps.
 * Supports: # H1, ## H2, ### H3, **bold**, *italic*, `code`, [links](url),
 * unordered lists (- item), ordered lists (1. item), blockquotes (>),
 * paragraphs, and line breaks.
 * Sanitizes HTML by escaping < > & in the source first.
 */

function escape(html) {
  return String(html || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function renderMarkdown(src) {
  if (!src) return '';
  let text = escape(src);
  // Code (single line backticks)
  text = text.replace(/`([^`\n]+)`/g, '<code class="bg-stone-100 text-stone-800 px-1 py-0.5 rounded text-sm font-mono">$1</code>');
  // Bold + italic
  text = text.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  text = text.replace(/(^|\s)\*([^*\n]+)\*/g, '$1<em>$2</em>');
  // Inline video — !video[poster.jpg](video.mp4) — autoplay muted, native player
  text = text.replace(/!video\[([^\]]*)\]\(([^)\s]+)\)/g, (_m, poster, video) =>
    `<video src="${video}" ${poster ? `poster="${poster}" ` : ''}controls muted playsinline preload="metadata" class="w-full rounded-2xl my-6 shadow-xl bg-stone-900"></video>`
  );
  // Inline image — ![alt](url)
  text = text.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (_m, alt, url) =>
    `<img src="${url}" alt="${alt}" class="w-full rounded-2xl my-6 shadow-md" loading="lazy" />`
  );
  // Links
  text = text.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-amber-700 hover:underline">$1</a>');

  const lines = text.split('\n');
  const out = [];
  let inUl = false, inOl = false, inP = [];
  const flushParagraph = () => {
    if (inP.length) {
      out.push(`<p class="mb-4 leading-relaxed text-stone-700">${inP.join(' ')}</p>`);
      inP = [];
    }
  };
  const closeLists = () => {
    if (inUl) { out.push('</ul>'); inUl = false; }
    if (inOl) { out.push('</ol>'); inOl = false; }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    if (!trimmed) { flushParagraph(); closeLists(); continue; }
    let m;
    if ((m = /^###\s+(.*)$/.exec(trimmed))) {
      flushParagraph(); closeLists();
      out.push(`<h3 class="text-xl font-bold text-stone-900 mt-6 mb-2">${m[1]}</h3>`);
    } else if ((m = /^##\s+(.*)$/.exec(trimmed))) {
      flushParagraph(); closeLists();
      out.push(`<h2 class="text-2xl font-bold text-stone-900 mt-8 mb-3" style="font-family:Barlow Condensed,sans-serif">${m[1]}</h2>`);
    } else if ((m = /^#\s+(.*)$/.exec(trimmed))) {
      flushParagraph(); closeLists();
      out.push(`<h1 class="text-3xl font-bold text-stone-900 mt-8 mb-4" style="font-family:Barlow Condensed,sans-serif">${m[1]}</h1>`);
    } else if ((m = /^>\s+(.*)$/.exec(trimmed))) {
      flushParagraph(); closeLists();
      out.push(`<blockquote class="border-l-4 border-amber-400 pl-4 italic text-stone-600 my-4">${m[1]}</blockquote>`);
    } else if ((m = /^[-*]\s+(.*)$/.exec(trimmed))) {
      flushParagraph();
      if (!inUl) { closeLists(); out.push('<ul class="list-disc pl-6 my-3 space-y-1 text-stone-700">'); inUl = true; }
      out.push(`<li>${m[1]}</li>`);
    } else if ((m = /^\d+\.\s+(.*)$/.exec(trimmed))) {
      flushParagraph();
      if (!inOl) { closeLists(); out.push('<ol class="list-decimal pl-6 my-3 space-y-1 text-stone-700">'); inOl = true; }
      out.push(`<li>${m[1]}</li>`);
    } else {
      closeLists();
      inP.push(trimmed);
    }
  }
  flushParagraph();
  closeLists();
  return out.join('\n');
}
